import { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SDGMapping = () => {
  const [sdgMappings, setSdgMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSDGMappings();
  }, []);

  const fetchSDGMappings = async () => {
    try {
      const response = await axios.get(`${API}/sdg-mapping`);
      setSdgMappings(response.data);
    } catch (error) {
      console.error('Error fetching SDG mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const sdgColors = {
    6: '#26BDE2',
    7: '#FCC30B',
    8: '#A21942',
    12: '#BF8B2E',
    13: '#3F7E44'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="sdg-mapping-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          SDG Mapping
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          See how your ESG initiatives contribute to UN Sustainable Development Goals
        </p>
      </div>

      {/* Overview Card */}
      <Card className="border-[#E5E1D8] mb-8" data-testid="sdg-overview-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-[#1A362D]" />
            <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Your SDG Contribution</CardTitle>
          </div>
          <CardDescription>
            Your business activities are aligned with {sdgMappings.length} UN Sustainable Development Goals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {sdgMappings.map((sdg) => (
              <div
                key={sdg.sdg_number}
                className="flex flex-col items-center p-4 bg-white rounded-lg border-2 hover:shadow-sm transition-all duration-200"
                style={{ borderColor: sdgColors[sdg.sdg_number] || '#E5E1D8' }}
                data-testid={`sdg-icon-${sdg.sdg_number}`}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-2"
                  style={{ backgroundColor: sdgColors[sdg.sdg_number] || '#1A362D' }}
                >
                  {sdg.sdg_number}
                </div>
                <p className="text-xs text-center text-[#6B7280] leading-tight">SDG {sdg.sdg_number}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Mappings */}
      <div className="grid grid-cols-1 gap-6">
        {sdgMappings.map((sdg, index) => (
          <Card key={index} className="border-[#E5E1D8] overflow-hidden" data-testid={`sdg-detail-${sdg.sdg_number}`}>
            <div className="flex">
              {/* SDG Badge */}
              <div
                className="w-32 flex flex-col items-center justify-center text-white p-6"
                style={{ backgroundColor: sdgColors[sdg.sdg_number] || '#1A362D' }}
              >
                <p className="text-5xl font-bold mb-2">{sdg.sdg_number}</p>
                <p className="text-xs text-center uppercase tracking-wider">SDG</p>
              </div>

              {/* Content */}
              <div className="flex-1 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-[#111814] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {sdg.sdg_name}
                    </h3>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{sdg.description}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-[#2D4A3E]/10 px-4 py-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-[#2D4A3E]" />
                    <span className="text-2xl font-bold text-[#2D4A3E]">{sdg.contribution}%</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-[#6B7280]">Contribution Level</span>
                    <span className="font-semibold text-[#111814]">{sdg.contribution}%</span>
                  </div>
                  <Progress value={sdg.contribution} className="h-3" data-testid={`sdg-progress-${sdg.sdg_number}`} />
                </div>

                {/* Action items based on SDG */}
                <div className="mt-4 p-3 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">How to Improve</p>
                  <p className="text-sm text-[#4B5563]">
                    {sdg.sdg_number === 7 && 'Increase renewable energy adoption, improve energy efficiency, and track energy consumption more frequently.'}
                    {sdg.sdg_number === 6 && 'Implement water recycling systems, reduce water waste, and monitor usage patterns.'}
                    {sdg.sdg_number === 12 && 'Adopt circular economy practices, reduce waste generation, and source sustainable materials.'}
                    {sdg.sdg_number === 8 && 'Enhance worker safety programs, provide skill development, and ensure fair labor practices.'}
                    {sdg.sdg_number === 13 && 'Set carbon reduction targets, transition to clean energy, and implement climate adaptation strategies.'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Summary Stats */}
      <Card className="border-[#E5E1D8] mt-8" data-testid="sdg-summary">
        <CardHeader>
          <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Impact Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-[#F9F8F6] rounded-lg">
              <p className="text-3xl font-bold text-[#1A362D] mb-1">{sdgMappings.length}</p>
              <p className="text-sm text-[#6B7280]">SDGs Addressed</p>
            </div>
            <div className="text-center p-4 bg-[#F9F8F6] rounded-lg">
              <p className="text-3xl font-bold text-[#1A362D] mb-1">
                {Math.round(sdgMappings.reduce((sum, sdg) => sum + sdg.contribution, 0) / sdgMappings.length)}%
              </p>
              <p className="text-sm text-[#6B7280]">Average Contribution</p>
            </div>
            <div className="text-center p-4 bg-[#F9F8F6] rounded-lg">
              <p className="text-3xl font-bold text-[#1A362D] mb-1">
                {sdgMappings.filter(sdg => sdg.contribution >= 75).length}
              </p>
              <p className="text-sm text-[#6B7280]">High Impact SDGs</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SDGMapping;
